/**
 * X-Ray money formatting. Kept pure and dependency-free, and covered by
 * src/lib/__tests__/xray-pricing.test.ts - what an admin types here becomes the
 * per-lead price every agency on that plan is charged.
 *
 * The model: each product has a per-lead price PER PLAN, stored in cents
 * (xray_plan_prices). Nothing is computed from a rate any more, so there is no
 * price math here, only formatting and parsing.
 */

/** Cents as "$X.XX". Returns "-" when there is no number to show. */
export const formatDollarsFromCents = (cents: number | null | undefined): string =>
  cents == null || !Number.isFinite(cents) ? '-' : `$${(cents / 100).toFixed(2)}`

/**
 * Cents as the shortest honest label: whole dollars lose the ".00", and an
 * amount under a dollar reads in cents (55¢, never $0.55).
 */
export const formatMoney = (cents: number | null | undefined): string => {
  if (cents == null || !Number.isFinite(cents)) return '-'
  const abs = Math.abs(cents)
  const sign = cents < 0 ? '-' : ''
  if (abs > 0 && abs < 100) return `${sign}${abs}¢`
  if (abs % 100 === 0) return `${sign}$${(abs / 100).toLocaleString()}`
  return `${sign}$${(abs / 100).toFixed(2)}`
}

/** Cents as a bare editable dollar string ("295" -> "2.95"). */
export const centsToInput = (cents: number): string => (cents / 100).toFixed(2)

/** Dollars typed by a person ("2.95") as integer cents, or null when unparseable. */
export const parseDollarsToCents = (input: string): number | null => {
  const trimmed = input.trim().replace(/^\$/, '')
  if (!/^\d*\.?\d{0,2}$/.test(trimmed) || trimmed === '' || trimmed === '.') return null
  const cents = Math.round(Number(trimmed) * 100)
  return Number.isFinite(cents) ? cents : null
}

/**
 * A product key's name in an agency's spend breakdown. The agency summary
 * carries the plan's price map (keyed by product key) but not the catalog
 * names, so unknown keys fall back to the key rather than fetch the catalog.
 */
export function xrayProductLabel(key: string): string {
  const LABELS: Record<string, string> = {
    enrichment: 'Xray Enrich',
    credit_check: 'Xray Finance',
    debt_income: 'Debt to income and income',
  }
  return LABELS[key] ?? key
}
